import {useCallback, useEffect, useId, useRef, useState} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {clsx} from 'clsx'

import styles from './BackgroundGrid.module.css'
import {
  areGridDimensionsEqual,
  getEvictedTrailPixelIds,
  getGridDimensions,
  getTrailGridPosition,
  shouldCreateTrailPixel,
  type GridDimensions,
  type GridPosition,
} from './background-grid-helpers'
import {
  BACKGROUND_COLOR,
  COLORS,
  CORNER_DOTS,
  DARK_BACKGROUND_COLOR,
  DARK_GRID_LINE_COLOR,
  GRID_LINE_COLOR,
} from './config'
import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'

const CELL_SIZE = 1
const TRAIL_EXIT_DURATION_MS = 100

type TrailPixel = {
  id: number
  x: number
  y: number
  color: string
  exiting: boolean
}

type TrailPixelTimeouts = {
  exit: number
  positionIndex: number
  removal: number
}

export type BackgroundGridProps = {
  className?: string
  dimTrailAreaId?: string
  hasCornerDots?: boolean
  hasCursorTrain?: boolean
  theme?: 'light' | 'dark'
}

function randomColor() {
  return COLORS[Math.floor(Math.random() * COLORS.length)] ?? COLORS[0]
}

export function BackgroundGrid({
  className,
  dimTrailAreaId,
  hasCornerDots = false,
  hasCursorTrain = true,
  theme = 'light',
}: BackgroundGridProps) {
  const backgroundColor = theme === 'dark' ? DARK_BACKGROUND_COLOR : BACKGROUND_COLOR
  const gridLineColor = theme === 'dark' ? DARK_GRID_LINE_COLOR : GRID_LINE_COLOR
  const backgroundGridId = useId().replace(/:/g, '')
  const gradientId = `background-grid-gradient-${backgroundGridId}`
  const patternId = `background-grid-pattern-${backgroundGridId}`
  const wrapperRef = useRef<HTMLDivElement>(null)
  const nextTrailPixelIdRef = useRef(0)
  const trailPixelIdsRef = useRef<number[]>([])
  const activeTrailPositionsRef = useRef<Set<number>>(new Set())
  const lastTrailPositionRef = useRef<GridPosition | null>(null)
  const trailTimeoutsRef = useRef<Map<number, TrailPixelTimeouts>>(new Map())
  const dimTrailAreaRef = useRef<HTMLElement | null>(null)

  const [trailPixels, setTrailPixels] = useState<TrailPixel[]>([])
  const [gridState, setGridState] = useState<GridDimensions>(() => getGridDimensions(0, 0))

  const {cellSizePx, height: gridHeight, width: gridWidth} = gridState
  const prefersReducedMotion = usePrefersReducedMotion()
  const canShowCursorTrain = hasCursorTrain && !prefersReducedMotion
  const canShowCursorTrainRef = useRef<boolean>(canShowCursorTrain)

  const trailContext = `${gridWidth}:${gridHeight}:${cellSizePx}:${hasCursorTrain}:${prefersReducedMotion}`
  const [previousTrailContext, setPreviousTrailContext] = useState(trailContext)

  const {isIntersecting} = useIntersectionObserver(wrapperRef)
  const isIntersectingRef = useRef<boolean>(isIntersecting)

  if (previousTrailContext !== trailContext) {
    setPreviousTrailContext(trailContext)
    setTrailPixels([])
  }

  const clearTrailPixelTimeout = useCallback((id: number) => {
    const timeouts = trailTimeoutsRef.current.get(id)
    if (timeouts !== undefined) {
      window.clearTimeout(timeouts.exit)
      window.clearTimeout(timeouts.removal)
      activeTrailPositionsRef.current.delete(timeouts.positionIndex)
      trailTimeoutsRef.current.delete(id)
    }
  }, [])

  const clearTrail = useCallback(() => {
    for (const id of [...trailTimeoutsRef.current.keys()]) {
      clearTrailPixelTimeout(id)
    }
    trailPixelIdsRef.current = []
    activeTrailPositionsRef.current.clear()
    lastTrailPositionRef.current = null
  }, [clearTrailPixelTimeout])

  const removeTrailPixel = useCallback(
    (id: number) => {
      clearTrailPixelTimeout(id)
      trailPixelIdsRef.current = trailPixelIdsRef.current.filter(pixelId => pixelId !== id)
      setTrailPixels(currentPixels => currentPixels.filter(pixel => pixel.id !== id))
    },
    [clearTrailPixelTimeout],
  )

  const addTrailPixel = useCallback(
    (x: number, y: number) => {
      const positionIndex = y * gridWidth + x
      if (activeTrailPositionsRef.current.has(positionIndex)) {
        return
      }

      const id = nextTrailPixelIdRef.current++
      const exitDelay = 100 + 100 * Math.random()
      activeTrailPositionsRef.current.add(positionIndex)
      const exitTimeout = window.setTimeout(() => {
        setTrailPixels(currentPixels =>
          currentPixels.map(pixel => (pixel.id === id ? {...pixel, exiting: true} : pixel)),
        )
      }, exitDelay)
      const removalTimeout = window.setTimeout(() => {
        removeTrailPixel(id)
      }, exitDelay + TRAIL_EXIT_DURATION_MS)

      trailTimeoutsRef.current.set(id, {
        exit: exitTimeout,
        positionIndex,
        removal: removalTimeout,
      })
      trailPixelIdsRef.current.push(id)
      const evictedIds = getEvictedTrailPixelIds(trailPixelIdsRef.current)

      if (evictedIds.length > 0) {
        trailPixelIdsRef.current = trailPixelIdsRef.current.slice(evictedIds.length)
        for (const evictedId of evictedIds) {
          clearTrailPixelTimeout(evictedId)
        }
      }

      setTrailPixels(currentPixels => {
        const retainedPixels = evictedIds.length
          ? currentPixels.filter(pixel => !evictedIds.includes(pixel.id))
          : currentPixels

        return [
          ...retainedPixels,
          {
            id,
            x,
            y,
            color: randomColor(),
            exiting: false,
          },
        ]
      })
    },
    [clearTrailPixelTimeout, gridWidth, removeTrailPixel],
  )

  const handlePointerMove = useCallback(
    (pointerPosition: {x: number; y: number}, rect: DOMRect) => {
      if (!canShowCursorTrainRef.current || !wrapperRef.current || !isIntersectingRef.current) {
        return
      }

      const {x, y} = pointerPosition
      const position = getTrailGridPosition(x, y, rect, gridWidth, gridHeight, cellSizePx)

      if (position === null) {
        return
      }

      if (
        !shouldCreateTrailPixel(
          lastTrailPositionRef.current,
          position,
          prefersReducedMotion,
          activeTrailPositionsRef.current.has(position.y * gridWidth + position.x),
        )
      ) {
        lastTrailPositionRef.current = position
        return
      }

      lastTrailPositionRef.current = position
      addTrailPixel(position.x, position.y)
    },
    [addTrailPixel, cellSizePx, gridHeight, gridWidth, prefersReducedMotion],
  )

  const handlePointerMoveGlobal = useCallback(
    (event: PointerEvent) => {
      if (!canShowCursorTrainRef.current || !wrapperRef.current || !isIntersectingRef.current) {
        return
      }

      const pointerPosition: {x: number; y: number} = {
        x: event.clientX,
        y: event.clientY,
      }

      const rect = wrapperRef.current.getBoundingClientRect()
      const {x, y} = pointerPosition
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        // If we're also within the area the trail should go dim,
        // add a data attribute for CSS to process
        if (dimTrailAreaRef.current) {
          const dimTrailAreaRect = dimTrailAreaRef.current.getBoundingClientRect()
          const shouldDim =
            x >= dimTrailAreaRect.left &&
            x <= dimTrailAreaRect.right &&
            y >= dimTrailAreaRect.top &&
            y <= dimTrailAreaRect.bottom

          if (wrapperRef.current.hasAttribute('data-dim-trail') !== shouldDim) {
            wrapperRef.current.toggleAttribute('data-dim-trail', shouldDim)
          }
        }

        handlePointerMove({x, y}, rect)
      }
    },
    [handlePointerMove],
  )

  const handlePointerLeave = useCallback(() => {
    lastTrailPositionRef.current = null
  }, [])

  useEffect(() => {
    const wrapper = wrapperRef.current
    const parent = wrapper?.parentElement

    if (!parent) {
      return
    }

    const updateGridDimensions = ({height, width}: Pick<DOMRect, 'height' | 'width'>) => {
      const nextDimensions = getGridDimensions(width, height)
      setGridState(currentState =>
        areGridDimensionsEqual(currentState, nextDimensions) ? currentState : nextDimensions,
      )
    }

    updateGridDimensions(parent.getBoundingClientRect())

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) updateGridDimensions(entry.contentRect)
    })
    resizeObserver.observe(parent)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    clearTrail()
  }, [clearTrail, trailContext])

  useEffect(() => {
    canShowCursorTrainRef.current = canShowCursorTrain
    if (!canShowCursorTrain) return

    window.addEventListener('pointermove', handlePointerMoveGlobal)

    return () => {
      window.removeEventListener('pointermove', handlePointerMoveGlobal)
    }
  }, [canShowCursorTrain, handlePointerMoveGlobal])

  useEffect(() => {
    isIntersectingRef.current = isIntersecting
  }, [isIntersecting])

  useEffect(() => {
    dimTrailAreaRef.current = document.getElementById(dimTrailAreaId || '')
  }, [dimTrailAreaId])

  useEffect(() => {
    const trailPixelIds = trailPixelIdsRef.current
    const activeTrailPositions = activeTrailPositionsRef.current
    const trailTimeouts = trailTimeoutsRef.current

    return () => {
      for (const {exit, removal} of trailTimeouts.values()) {
        window.clearTimeout(exit)
        window.clearTimeout(removal)
      }
      trailPixelIds.length = 0
      activeTrailPositions.clear()
      trailTimeouts.clear()
    }
  }, [])

  return (
    <div
      className={clsx(styles.Wrapper, className)}
      data-color-mode={theme}
      onPointerLeave={handlePointerLeave}
      ref={wrapperRef}
    >
      {gridWidth > 0 && gridHeight > 0 && (
        <svg
          aria-hidden="true"
          className={styles.Svg}
          height={gridHeight * cellSizePx}
          preserveAspectRatio="none"
          viewBox={`0 0 ${gridWidth} ${gridHeight}`}
          width={gridWidth * cellSizePx}
        >
          <defs>
            <pattern id={patternId} height="1" patternUnits="userSpaceOnUse" width="1">
              <path className={styles.GridLine} d="M 1 0 H 0 V 1" stroke={gridLineColor} />
            </pattern>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor={backgroundColor} stopOpacity="1" />
              <stop offset="0.5" stopColor={backgroundColor} stopOpacity="0" />
              <stop offset="1" stopColor={backgroundColor} stopOpacity="0" />
            </linearGradient>
          </defs>

          <rect fill={backgroundColor} height={gridHeight} width={gridWidth} />
          <rect fill={`url(#${patternId})`} height={gridHeight} width={gridWidth} />
          <path className={styles.GridLine} d={`M ${gridWidth} 0 V ${gridHeight} H 0`} stroke={gridLineColor} />
          <rect fill={`url(#${gradientId})`} height={gridHeight} width={gridWidth} />
          {hasCornerDots &&
            gridWidth > 3 &&
            gridHeight > 3 &&
            Object.entries(CORNER_DOTS).map(([corner, dots]) => (
              <g
                key={corner}
                className={styles.CornerDots}
                data-animate={!prefersReducedMotion}
                transform={`translate(${corner === 'left' ? CELL_SIZE * 2 : gridWidth - CELL_SIZE * 4} ${CELL_SIZE * 2})`}
              >
                {dots.map(({x, y, color}, index) => (
                  <rect
                    key={`${x}-${y}`}
                    className={styles.CornerDot}
                    data-static={corner === 'left' ? x === y : x !== y}
                    style={{'--corner-dot-index': index}}
                    fill={color}
                    height={CELL_SIZE}
                    width={CELL_SIZE}
                    x={x * CELL_SIZE}
                    y={y * CELL_SIZE}
                  />
                ))}
              </g>
            ))}
          <g className={styles.Trail}>
            {trailPixels.map(pixel => (
              <rect
                className={clsx(styles.TrailPixel, pixel.exiting && styles.TrailPixelExiting)}
                fill={pixel.color}
                height={CELL_SIZE}
                key={pixel.id}
                width={CELL_SIZE}
                x={pixel.x}
                y={pixel.y}
              />
            ))}
          </g>
        </svg>
      )}
    </div>
  )
}
